import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { CONCEPT_GALLERY, preselectServiceAndQuote } from "../data/site";

const TOTAL = CONCEPT_GALLERY.length;

const ArrowBtn = ({ testid, side, onClick, children }) => (
  <button data-testid={testid} onClick={(e) => { e.stopPropagation(); onClick(); }} className={`absolute ${side} top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-white/20 flex items-center justify-center text-white hover:border-[#FF6B00] hover:text-[#FF6B00] transition-colors`}>
    {children}
  </button>
);

const Lightbox = ({ index, onClose, onStep }) => {
  const item = CONCEPT_GALLERY[index];
  return (
    <motion.div data-testid="concept-lightbox" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 z-10 bg-black/95 flex items-center justify-center p-4 sm:p-10" onClick={onClose}>
      <ArrowBtn testid="concept-lightbox-prev" side="left-3 sm:left-6" onClick={() => onStep(-1)}><ChevronLeft size={20} /></ArrowBtn>
      <motion.img key={item.image} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} src={item.image} alt={`${item.title} — concept render by VIC WRAP STUDIO`} className="max-h-full max-w-full object-contain rounded-xl" onClick={(e) => e.stopPropagation()} />
      <ArrowBtn testid="concept-lightbox-next" side="right-3 sm:right-6" onClick={() => onStep(1)}><ChevronRight size={20} /></ArrowBtn>
      <div className="absolute bottom-5 inset-x-0 text-center">
        <div className="font-tech text-[10px] uppercase tracking-[0.2em] text-[#FF6B00]">{item.tag} · {index + 1}/{TOTAL}</div>
        <div className="font-display text-lg font-bold uppercase tracking-wide">{item.title}</div>
      </div>
    </motion.div>
  );
};

const Header = ({ onClose }) => (
  <div className="flex items-start justify-between gap-4 px-6 sm:px-10 pt-7 pb-5 border-b border-white/5">
    <div>
      <div className="eyebrow mb-2">CONCEPT GALLERY</div>
      <h3 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight leading-[0.95]">
        See it <span className="text-gradient">before we build it</span>
      </h3>
      <p className="mt-2 text-zinc-500 text-sm max-w-xl">Pre-build renders and design studies from the studio. Every concept is signed off before a single sheet of vinyl is cut.</p>
    </div>
    <button data-testid="concept-gallery-close" onClick={onClose} aria-label="Close gallery" className="shrink-0 w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-zinc-300 hover:text-white hover:border-[#FF6B00] hover:rotate-90 transition-all duration-300">
      <X size={18} />
    </button>
  </div>
);

const Tile = ({ item, i, onOpen }) => (
  <motion.button data-testid={`concept-gallery-item-${i}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }} onClick={onOpen} className="group relative rounded-2xl overflow-hidden border border-white/10 bg-[#18181B] text-left">
    <img src={item.image} alt={`${item.title} — VIC WRAP STUDIO`} loading="lazy" className="w-full h-56 sm:h-64 object-cover transition-transform duration-700 ease-out group-hover:scale-[1.08]" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
    <div className="absolute bottom-0 inset-x-0 p-4">
      <div className="font-tech text-[10px] uppercase tracking-[0.2em] text-[#FF6B00] mb-1">{item.tag}</div>
      <div className="font-display text-lg font-bold uppercase tracking-wide leading-tight">{item.title}</div>
    </div>
    <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/10 group-hover:ring-[#FF6B00]/50 transition-shadow duration-300" />
  </motion.button>
);

const Footer = ({ onClose }) => (
  <div className="px-6 sm:px-10 py-5 border-t border-white/5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
    <span className="text-zinc-500 text-xs font-tech uppercase tracking-[0.2em]">Want a concept for your project?</span>
    <button data-testid="concept-gallery-quote-cta" onClick={() => { onClose(); preselectServiceAndQuote("Concept & Creative Design"); }} className="btn-primary text-sm">
      Request a free concept
    </button>
  </div>
);

const useGalleryKeys = (open, active, setActive, onClose, step) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") active === null ? onClose() : setActive(null);
      if (active !== null && e.key === "ArrowRight") step(1);
      if (active !== null && e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    window.__lenis?.stop();
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      window.__lenis?.start();
      document.body.style.overflow = "";
    };
  }, [open, active, setActive, onClose, step]);
};

export const ConceptGallery = ({ open, onClose }) => {
  const [active, setActive] = useState(null);
  const step = useCallback((d) => setActive((i) => (i + d + TOTAL) % TOTAL), []);
  useGalleryKeys(open, active, setActive, onClose, step);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div data-testid="concept-gallery-modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-3 sm:p-8" onClick={onClose}>
          <motion.div initial={{ y: 40, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 40, opacity: 0, scale: 0.98 }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }} onClick={(e) => e.stopPropagation()} className="relative w-full max-w-6xl max-h-[92vh] overflow-hidden rounded-3xl bg-[#0D0D0F] border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.8)] flex flex-col">
            <Header onClose={onClose} />
            <div className="overflow-y-auto px-6 sm:px-10 py-6">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {CONCEPT_GALLERY.map((item, i) => <Tile key={item.image} item={item} i={i} onOpen={() => setActive(i)} />)}
              </div>
            </div>
            <Footer onClose={onClose} />
            <AnimatePresence>
              {active !== null && <Lightbox index={active} onClose={() => setActive(null)} onStep={step} />}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
